'use client';
import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './button';
import { CountBadge } from './count-badge';
import { cn } from '../lib/utils';
import {
  controlFrameClassName,
  controlSizeClassName,
  type ControlSize,
  type ControlDensity,
} from './control-styles';

/** Date-only strings stay date-only; no tenant instant or timezone is inferred. */
export function formatDateRange(start: string, end: string): string {
  const parse = (value: string) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) throw new RangeError('Expected a date-only value');
    const date = new Date(`${value}T00:00:00Z`);
    if (
      !Number.isFinite(date.getTime()) ||
      date.getUTCFullYear() !== Number(value.slice(0, 4)) ||
      date.getUTCMonth() + 1 !== Number(value.slice(5, 7)) ||
      date.getUTCDate() !== Number(value.slice(8, 10))
    )
      throw new RangeError('Invalid date');
    return date;
  };
  const first = parse(start),
    last = parse(end);
  if (first > last) throw new RangeError('End precedes start');
  const full = new Intl.DateTimeFormat('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
  if (start === end) return full.format(first);
  if (start.slice(0, 7) === end.slice(0, 7)) return `${first.getUTCDate()}–${full.format(last)}`;
  const short = new Intl.DateTimeFormat('en-AU', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
  return `${start.slice(0, 4) === end.slice(0, 4) ? short.format(first) : full.format(first)}–${full.format(last)}`;
}
export function ExceptionsTrigger({
  count,
  onClick,
}: {
  count: number | null;
  onClick: () => void;
}) {
  if (count === 0) return null;
  return (
    <Button type="button" variant="secondary" size="toolbar" onClick={onClick}>
      Exceptions <CountBadge compact count={count} label="exceptions" />
    </Button>
  );
}
export function DateNav({
  label,
  onPrevious,
  onToday,
  onNext,
  view,
  filters,
  tools,
  exceptions,
  size = 'toolbar',
  density,
}: {
  label: string;
  onPrevious: () => void;
  onToday: () => void;
  onNext: () => void;
  view?: React.ReactNode;
  filters?: React.ReactNode;
  tools?: React.ReactNode;
  exceptions?: React.ReactNode;
  size?: ControlSize;
  density?: ControlDensity;
}) {
  return (
    <div
      role="group"
      aria-label="Date navigation"
      className="flex min-w-0 flex-wrap items-center gap-2 lg:flex-nowrap"
    >
      <div
        className={cn(
          controlFrameClassName,
          controlSizeClassName(size, density),
          'inline-flex shrink-0 items-center border-border p-0',
        )}
      >
        <Button
          type="button"
          variant="tertiary"
          size={size}
          density={density}
          className="h-full min-h-0 border-0 py-0"
          aria-label="Previous period"
          onClick={onPrevious}
        >
          <ChevronLeft aria-hidden="true" />
        </Button>
        <Button
          type="button"
          variant="tertiary"
          size={size}
          density={density}
          className="h-full min-h-0 border-0 py-0"
          onClick={onToday}
        >
          Today
        </Button>
        <Button
          type="button"
          variant="tertiary"
          size={size}
          density={density}
          className="h-full min-h-0 border-0 py-0"
          aria-label="Next period"
          onClick={onNext}
        >
          <ChevronRight aria-hidden="true" />
        </Button>
      </div>
      <p className="min-w-0 font-body text-body font-medium tabular-nums">{label}</p>
      {view}
      {exceptions}
      <div className="ml-auto flex items-center gap-2">
        {filters}
        {tools}
      </div>
    </div>
  );
}
